<div class="flex h-full min-h-0 w-full flex-col">
  <!-- header -->
  <div class="flex items-start justify-between px-6" [ngClass]="shortScreen ? 'pt-3' : 'pt-6'">
    <div class="min-w-0">
      <h2 class="text-xl font-bold">{{ texts.title | t }}</h2>
      @if (!shortScreen) {
      <p class="text-secondary text-sm">{{ texts.subtitle | t }}</p>
      }
    </div>
    <button mat-icon-button type="button" (click)="cancel()">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  @if (hasWarnings) {
  <div class="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:bg-amber-900/20">
    <mat-icon class="icon-size-5 shrink-0 text-amber-600">warning</mat-icon>
    <span class="text-sm">{{ texts.limitExceededWarning | t }}</span>
  </div>
  }

  <!-- contenido -->
  <div class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-4">
    @for (group of groups; track group.index) {
    <div class="flex flex-col gap-3">
      @if (showsDocumentLabels) {
      <div class="flex items-center gap-2">
        <mat-icon class="icon-size-5 text-secondary">description</mat-icon>
        <span class="font-semibold">{{ group.label }}</span>
      </div>
      }

      @if (group.lines.length) {
      <div class="flex flex-col gap-2">
        <span class="text-secondary text-xs font-semibold uppercase">{{ texts.packagingSection | t }}</span>
        @for (row of group.lines; track row.key) {
        <ng-container
          *ngTemplateOutlet="requestCard; context: { $implicit: row, group: group }"></ng-container>
        }
      </div>
      }

      @if (group.containers.length) {
      <div class="flex flex-col gap-2">
        <span class="text-secondary text-xs font-semibold uppercase">{{ texts.containersSection | t }}</span>
        @for (row of group.containers; track row.key) {
        <ng-container
          *ngTemplateOutlet="requestCard; context: { $implicit: row, group: group }"></ng-container>
        }
      </div>
      }
    </div>
    }
  </div>

  <!-- acciones -->
  <div class="flex flex-wrap items-center justify-between gap-3 border-t px-6"
    [ngClass]="shortScreen ? 'py-2' : 'py-4'">
    <span class="text-secondary text-sm">
      @if (selectedCount) {
        {{ 'DOCUMENT_GENERATE.PACK_LOAN_MODAL.SELECTED_COUNT' | t: { count: selectedCount } }}
      } @else {
        {{ texts.emptySelection | t }}
      }
    </span>
    <div class="flex items-center gap-2">
      <button mat-stroked-button type="button" (click)="cancel()">{{ texts.cancel | t }}</button>
      <button mat-flat-button color="primary" type="button" [disabled]="!canConfirm" (click)="confirm()">
        {{ texts.confirm | t }}
      </button>
    </div>
  </div>
</div>

<!-- tarjeta de una solicitud; empaque y contenedor comparten forma -->
<ng-template #requestCard let-row let-group="group">
  <div class="flex flex-col gap-3 rounded-xl border px-4 py-3"
    [class.border-red-400]="row.selected && (hasInvalidQuantity(row) || missingReason(row) || duplicatedReason(row) || (blocksOnExcess && exceedsLimit(row)))">
    <div class="flex items-start gap-3">
      <mat-checkbox [(ngModel)]="row.selected" (ngModelChange)="onToggle(row)"></mat-checkbox>
      <div class="min-w-0 flex-1">
        <div class="truncate font-semibold">{{ row.title }}</div>
        <div class="text-secondary truncate text-xs">{{ row.subtitle }}</div>
        @if (row.traceLabel) {
        <div class="text-secondary truncate text-xs">{{ row.traceLabel }}</div>
        }
      </div>
      <div class="flex flex-col items-end">
        <span class="text-secondary text-xs uppercase">{{ texts.suggested | t }}</span>
        <span class="font-semibold">
          {{ row.suggestion.systemQuantity | dgNumber:'stock' }}
          {{ row.suggestion.baseUnitOfMeasure.code }}
        </span>
      </div>
    </div>

    @if (row.selected) {
    <div class="grid grid-cols-1 gap-3 md:grid-cols-4">
      <mat-form-field appearance="fill" class="w-full">
        <mat-label>{{ texts.quantity | t }}</mat-label>
        <input matInput type="number" min="1" [max]="maxFor(row)" [ngModel]="row.quantityText"
          (ngModelChange)="onQuantityInput(row, $event)" />
      </mat-form-field>

      <mat-form-field appearance="fill" class="w-full md:col-span-2">
        <mat-label>{{ texts.reason | t }}</mat-label>
        <mat-select [(ngModel)]="row.reasonId">
          @for (reason of data.reasons; track reason._id) {
          <mat-option [value]="reason._id">{{ reason.code }} - {{ reason.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="fill" class="w-full">
        <mat-label>{{ texts.note | t }}</mat-label>
        <input matInput type="text" [(ngModel)]="row.note" />
      </mat-form-field>
    </div>

    <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
      @if (isFree(row)) {
      <span class="text-secondary">{{ texts.freeLoan | t }}</span>
      } @else {
      <span class="text-secondary">{{ texts.limit | t }}: {{ row.limit.loanLimit | dgNumber:'stock' }}</span>
      <span class="text-secondary">{{ texts.consigned | t }}: {{ row.limit.consigned | dgNumber:'stock' }}</span>
      <span class="text-secondary">{{ texts.accumulated | t }}: {{ row.limit.accumulated | dgNumber:'stock' }}</span>
      <span [class.text-red-500]="exceedsLimit(row)">
        {{ texts.available | t }}: {{ row.limit.available | dgNumber:'stock' }}
      </span>
      }
    </div>

    @if (hasInvalidQuantity(row)) {
    <span class="text-xs text-red-500">{{ texts.quantityInvalid | t }}</span>
    }
    @if (missingReason(row)) {
    <span class="text-xs text-red-500">{{ texts.reasonRequired | t }}</span>
    }
    @if (duplicatedReason(row)) {
    <span class="text-xs text-red-500">{{ texts.reasonDuplicated | t }}</span>
    }
    @if (exceedsLimit(row) && blocksOnExcess) {
    <span class="text-xs text-red-500">{{ texts.limitExceeded | t }}</span>
    }

    <div class="flex items-center gap-2">
      <button mat-stroked-button type="button" (click)="addRequestFor(group, row)"
        [matTooltip]="texts.addRequest | t">
        <mat-icon class="icon-size-5">add</mat-icon>
      </button>
      @if (isExtra(row)) {
      <button mat-stroked-button type="button" (click)="removeRequest(group, row)"
        [matTooltip]="texts.removeRequest | t">
        <mat-icon class="icon-size-5">delete</mat-icon>
      </button>
      }
    </div>
    }
  </div>
</ng-template>
